/**
 * Metadata parsing utilities
 * Functions for extracting and parsing metadata from filenames and patterns
 */

import { getAvailableFields as getFieldsFromDefinitions } from '../config/fieldDefinitions.jsx';

/**
 * Extract fields from filename using pattern
 * @param {string} filename - Full filename or path
 * @param {string} pattern - Pattern with placeholders like {project}
 * @returns {Object} - Extracted fields
 */
export const extractFieldsFromPattern = (filename, pattern) => {
  const fields = {};
  
  // Extract placeholders from pattern
  const placeholders = pattern.match(/\{([^}]+)\}/g) || [];
  const fieldNames = placeholders.map(p => p.slice(1, -1));
  
  // Create regex from pattern
  let regexPattern = pattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  fieldNames.forEach(field => {
    regexPattern = regexPattern.replace(`\\{${field}\\}`, '(.+?)');
  });
  regexPattern = '^' + regexPattern + '$';
  
  // Extract values
  const regex = new RegExp(regexPattern);
  const match = filename.match(regex);
  
  if (match) {
    fieldNames.forEach((field, index) => {
      fields[field] = match[index + 1];
    });
  }
  
  return fields;
};

/**
 * Build pattern preview from template and sample filename
 * @param {string} pattern - Pattern template
 * @param {string} sampleFilename - Sample filename to test against
 * @returns {Object} - Preview with extracted values
 */
export const buildPatternPreview = (pattern, sampleFilename) => {
  if (!pattern || !sampleFilename) {
    return { valid: false, fields: {} };
  }
  
  const fields = extractFieldsFromPattern(sampleFilename, pattern);
  const hasFields = Object.keys(fields).length > 0;
  
  return {
    valid: hasFields,
    fields,
    pattern,
    sample: sampleFilename
  };
};

/**
 * Validate pattern against multiple filenames
 * @param {string} pattern - Pattern to validate
 * @param {Array} filenames - Array of filenames to test
 * @returns {Object} - Validation result
 */
export const validatePattern = (pattern, filenames) => {
  const results = filenames.map(filename => {
    const fields = extractFieldsFromPattern(filename, pattern);
    return {
      filename,
      fields,
      success: Object.keys(fields).length > 0
    };
  });
  
  const successCount = results.filter(r => r.success).length;
  const successRate = filenames.length > 0 ? successCount / filenames.length : 0;
  
  return {
    valid: successRate > 0.8, // Consider valid if works for 80% of files
    successRate,
    results
  };
};

/**
 * Get available field placeholders
 * Maps UI field names to API field names
 * @returns {Array} - Array of field objects
 */
export const getAvailableFields = () => {
  // Use the unified field definitions
  const fields = getFieldsFromDefinitions();
  
  // Return the unified fields
  return fields;
};

/**
 * Parse comma-separated tags
 * @param {string} tagString - Comma-separated tags
 * @returns {Array} - Array of trimmed tags
 */
export const parseTags = (tagString) => {
  if (!tagString) return [];
  return tagString.split(',').map(tag => tag.trim()).filter(tag => tag);
};

/**
 * Generate pattern from example
 * @param {string} filename - Example filename
 * @param {Object} knownFields - Known field values
 * @returns {string} - Generated pattern
 */
export const generatePatternFromExample = (filename, knownFields) => {
  let pattern = filename;
  
  Object.entries(knownFields).forEach(([field, value]) => {
    if (value && pattern.includes(value)) {
      pattern = pattern.replace(value, `{${field}}`);
    }
  });
  
  return pattern;
};

/**
 * Apply extracted metadata to files
 * @param {Array} files - Array of file objects
 * @param {string} pattern - Extraction pattern
 * @returns {Array} - Files with extracted metadata
 */
export const applyMetadataToFiles = (files, pattern) => {
  return files.map(file => {
    const filename = file.relativePath || file.name;
    const extracted = extractFieldsFromPattern(filename, pattern);
    
    return {
      ...file,
      metadata: {
        ...file.metadata,
        ...extracted,
        tags: extracted.tags ? parseTags(extracted.tags) : file.metadata?.tags
      }
    };
  });
};

/**
 * Get fields that are in a pattern
 * @param {string} pattern - Pattern string
 * @returns {Array} - Array of field names found in pattern
 */
export const getFieldsInPattern = (pattern) => {
  const matches = pattern.match(/\{([^}]+)\}/g) || [];
  return matches.map(match => match.slice(1, -1));
};